import { useEffect, useState } from 'react';
import { fetchPlacementAds } from '../api/placementAdsApi';

const TTL_MS = 5 * 60 * 1000;

/** @type {Map<string, { data: object[], fetchedAt: number, inflight: Promise<object[]>|null, listeners: Set<(rows: object[]) => void>, timer: number|null }>} */
const poolCache = new Map();

function entryKey(slotKey, limit, category) {
  return `${slotKey}|${limit}|${category || ''}`;
}

function getEntry(slotKey, limit, category) {
  const key = entryKey(slotKey, limit, category);
  let entry = poolCache.get(key);
  if (!entry) {
    entry = { data: [], fetchedAt: 0, inflight: null, listeners: new Set(), timer: null };
    poolCache.set(key, entry);
  }
  return entry;
}

function refreshEntry(slotKey, limit, category) {
  const entry = getEntry(slotKey, limit, category);
  if (entry.inflight) return entry.inflight;
  entry.inflight = fetchPlacementAds(slotKey, limit, category)
    .then((rows) => {
      const list = Array.isArray(rows) ? rows : [];
      entry.data = list;
      entry.fetchedAt = Date.now();
      entry.inflight = null;
      entry.listeners.forEach((fn) => fn(list));
      return list;
    })
    .catch(() => {
      entry.inflight = null;
      return entry.data;
    });
  return entry.inflight;
}

/**
 * 화면 노출 CPM 광고 풀 로더 — 동일 slotKey·limit·category는 모듈 캐시로 공유하고,
 * 5분 TTL 안이면 재요청하지 않는다. 구독자(refcount)가 있을 때만 한 개의 인터벌이 돈다.
 * @param {string} slotKey
 * @param {number} [limit]
 * @param {string|null} [category] businesses.category 값 — local_listing_feed_cpm처럼 여러 업종
 *   화면이 슬롯을 공유할 때, 화면의 업종과 맞는 광고만 우선 노출하기 위한 필터. 안 넘기면 기존과 동일.
 * @returns {object[]}
 */
export function usePlacementAdPool(slotKey, limit = 6, category = null) {
  const [pool, setPool] = useState(() => {
    if (!slotKey) return [];
    return getEntry(slotKey, limit, category).data;
  });

  useEffect(() => {
    if (!slotKey) {
      setPool([]);
      return undefined;
    }

    const entry = getEntry(slotKey, limit, category);
    const onUpdate = (rows) => setPool(rows);
    entry.listeners.add(onUpdate);
    if (entry.data.length) setPool(entry.data);

    const stale = Date.now() - entry.fetchedAt > TTL_MS;
    if (stale) void refreshEntry(slotKey, limit, category);

    if (!entry.timer) {
      entry.timer = window.setInterval(() => {
        void refreshEntry(slotKey, limit, category);
      }, TTL_MS);
    }

    return () => {
      entry.listeners.delete(onUpdate);
      if (entry.listeners.size === 0 && entry.timer) {
        window.clearInterval(entry.timer);
        entry.timer = null;
      }
    };
  }, [slotKey, limit, category]);

  return pool;
}
